import { useEffect, useMemo, useState } from 'react';
import { ApiError, api, money, useQuery } from '../api.js';
import { Stepper } from '../controls.js';
import { Drawer, Spinner, useToast } from '../ui.js';
import type { Reservation } from '../../../shared/types.js';

interface Times {
  totalTime: number; pic: number; sic: number; solo: number;
  dualReceived: number; crossCountry: number; night: number;
  actualInstrument: number; simulatedInstrument: number;
  groundTrainer: number; groundInstruction: number;
  dayLandings: number; nightLandings: number;
  dayTakeoffs: number; nightTakeoffs: number;
  approaches: number; holds: number;
}
type FollowKey = 'dualReceived' | 'pic' | 'solo' | 'crossCountry' | 'night' | 'simulatedInstrument' | 'actualInstrument';

interface Defaults {
  reservation: Reservation;
  hobbsStart: number | null;
  /** `follows` names the columns that track the total time as the Hobbs is entered. */
  suggested: Times & { isGround: boolean; follows: Record<FollowKey, boolean> };
}

/** The hour columns, in the order a logbook page lays them out. */
const HOUR_FIELDS: { key: keyof Times; label: string; hint?: string }[] = [
  { key: 'dualReceived', label: 'Dual received' },
  { key: 'pic', label: 'Pilot in command' },
  { key: 'solo', label: 'Solo' },
  { key: 'crossCountry', label: 'Cross-country' },
  { key: 'night', label: 'Night' },
  { key: 'simulatedInstrument', label: 'Simulated instrument', hint: 'Hood or view-limiting device' },
  { key: 'actualInstrument', label: 'Actual instrument', hint: 'In cloud' },
  { key: 'groundTrainer', label: 'Simulator / FTD' },
];

const COUNT_FIELDS: { key: keyof Times; label: string }[] = [
  { key: 'dayLandings', label: 'Day landings' },
  { key: 'nightLandings', label: 'Night landings' },
  { key: 'approaches', label: 'Instrument approaches' },
  { key: 'holds', label: 'Holds' },
];

export function CloseOut({ id, onClose, onDone }: {
  id: number; onClose: () => void; onDone: () => void;
}): JSX.Element {
  const { data, error } = useQuery<Defaults>(`/schedule/reservations/${id}/close-out`);
  const toast = useToast();

  const [hobbsEnd, setHobbsEnd] = useState('');
  const [times, setTimes] = useState<Times | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [route, setRoute] = useState('');
  const [remarks, setRemarks] = useState('');
  const [endorsement, setEndorsement] = useState('');
  const [grade, setGrade] = useState<'S' | 'U' | 'I'>('S');
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  // Columns the instructor has typed into stop tracking the Hobbs.
  const [touched, setTouched] = useState<Set<keyof Times>>(new Set());

  // Seed the form from what the server suggests for this lesson.
  useEffect(() => {
    if (!data) return;
    setTimes(data.suggested);
    setTo(data.reservation.aircraftTail ? '' : '');
  }, [data]);

  const hobbsStart = data?.hobbsStart ?? null;
  // What the session was booked as. A flight is closed out as a flight and a
  // ground lesson as ground, so the form asks for one set of figures, not both.
  const isGround = data?.suggested.isGround ?? false;

  /** Total time follows the Hobbs, which is the number the instructor reads. */
  const totalFromHobbs = useMemo(() => {
    if (isGround || hobbsStart == null || hobbsEnd === '') return null;
    const end = Number(hobbsEnd);
    if (!Number.isFinite(end)) return null;
    return Math.round((end - hobbsStart) * 10) / 10;
  }, [hobbsEnd, hobbsStart, isGround]);

  /**
   * The Hobbs drives the total, and the total drives the columns the lesson
   * implies — dual for a dual lesson, solo for a solo one, cross-country and
   * night where the lesson says so. A column the instructor has typed into is
   * left exactly as they left it.
   */
  useEffect(() => {
    if (totalFromHobbs == null || !data) return;
    const follows = data.suggested.follows;
    setTimes((t) => {
      if (!t) return t;
      const next = { ...t, totalTime: totalFromHobbs };
      for (const k of Object.keys(follows) as FollowKey[]) {
        if (follows[k] && !touched.has(k)) next[k] = totalFromHobbs;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalFromHobbs, data, touched]);

  if (error) return <Drawer title="Close out" onClose={onClose}><div className="empty">{error}</div></Drawer>;
  if (!data || !times) return <Drawer title="Close out" onClose={onClose}><Spinner /></Drawer>;

  const r = data.reservation;
  const set = (key: keyof Times, value: number) => {
    setTouched((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
    setTimes((t) => (t ? { ...t, [key]: value } : t));
  };

  const parseHours = (raw: string): number => {
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 10) / 10 : 0;
  };

  // Local mirror of the server's arithmetic checks, so the button explains itself.
  const localProblems: string[] = [];
  if (isGround) {
    if (times.groundInstruction <= 0) localProblems.push('Enter the ground instruction time.');
  } else {
    if (hobbsEnd === '') localProblems.push('Enter the Hobbs reading at the end of the flight.');
    else if (hobbsStart != null && Number(hobbsEnd) < hobbsStart) localProblems.push('The ending Hobbs is lower than the reading at authorization.');
    else if (totalFromHobbs != null && totalFromHobbs <= 0) localProblems.push('The flight has no time on it.');
    if (times.solo > 0 && times.dualReceived > 0) localProblems.push('A flight is either solo or dual received, not both.');
    for (const f of HOUR_FIELDS) {
      if (totalFromHobbs != null && times[f.key] > totalFromHobbs) {
        localProblems.push(`${f.label} cannot exceed the total time of flight.`);
      }
    }
  }

  const submit = async () => {
    setBusy(true);
    setProblems([]);
    try {
      const result = await api.post<{
        invoiceId: number | null; invoiceNumber: string | null; invoiceTotalCents: number | null;
        studentEntryId: number | null; instructorEntryId: number | null;
      }>(
        `/schedule/reservations/${id}/close-out`,
        {
          isGround,
          hobbsEnd: isGround ? undefined : Number(hobbsEnd),
          ...times,
          fromAirport: from || undefined,
          toAirport: to || undefined,
          route: route || undefined,
          remarks: remarks || undefined,
          endorsement: endorsement || undefined,
          grade: r.lessonId ? grade : undefined,
        },
      );
      const books = [result.studentEntryId && 'the student', result.instructorEntryId && 'your'].filter(Boolean);
      const wrote = books.length
        ? `Written to ${books.join(' and ')} logbook${books.length > 1 ? 's' : ''}.`
        : '';
      const billed = result.invoiceNumber
        ? ` Invoice ${result.invoiceNumber} raised for ${money(result.invoiceTotalCents ?? 0)}.`
        : '';
      toast.ok('Lesson closed out', `${wrote}${billed}`.trim());
      onDone();
    } catch (err) {
      if (err instanceof ApiError) setProblems(err.details?.length ? err.details : [err.message]);
      else setProblems(['Something went wrong. Try again.']);
      setBusy(false);
    }
  };

  return (
    <Drawer
      title="Close out the lesson"
      subtitle={`${r.studentName || r.notes || r.kind} · ${new Date(r.startsAt).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}`}
      onClose={onClose}
      footer={
        <button className="btn btn--primary btn--block" disabled={busy || localProblems.length > 0} onClick={submit}>
          {busy ? 'Closing out…' : 'Close out and sign'}
        </button>
      }
    >
      <div className="closeout">
        <p className="closeout__hint" style={{ margin: 0 }}>
          Booked as {isGround ? 'ground instruction' : 'a flight'}
          {r.lessonId && r.lessonLabel ? ` · ${r.lessonLabel}` : ''}.
        </p>

        {!isGround && (
          <section className="closeout__section">
            <h3 className="closeout__h">Hobbs</h3>
            <div className="closeout__hobbs">
              <div className="field">
                <span>Out</span>
                <div className="closeout__ro">{hobbsStart != null ? hobbsStart.toFixed(1) : '—'}</div>
              </div>
              <label className="field">
                <span>In</span>
                <input className="input closeout__num" inputMode="decimal" value={hobbsEnd} autoFocus
                  placeholder={hobbsStart != null ? (hobbsStart + 1.5).toFixed(1) : '0.0'}
                  onChange={(e) => setHobbsEnd(e.target.value)} />
              </label>
              <div className="field">
                <span>Total time of flight</span>
                <div className="closeout__ro closeout__ro--strong">
                  {totalFromHobbs != null ? totalFromHobbs.toFixed(1) : '—'}
                </div>
              </div>
            </div>
            <p className="closeout__hint" style={{ marginTop: 8 }}>
              The difference is the time of flight, and it bills the aircraft wet.
            </p>
          </section>
        )}

        {!isGround && (
          <section className="closeout__section">
            <h3 className="closeout__h">Time logged</h3>
            <div className="closeout__grid">
              {HOUR_FIELDS.map((f) => (
                <label className="field" key={f.key}>
                  <span>{f.label}</span>
                  <input
                    className="input closeout__num"
                    inputMode="decimal"
                    value={String(times[f.key])}
                    onChange={(e) => set(f.key, parseHours(e.target.value))}
                  />
                  {f.hint && <span className="closeout__hint">{f.hint}</span>}
                </label>
              ))}
            </div>
            <p className="closeout__hint" style={{ marginTop: 8 }}>
              Prefilled from the lesson. No column may exceed the total time of flight.
            </p>
          </section>
        )}

        <section className="closeout__section">
          <h3 className="closeout__h">Ground instruction</h3>
          <div className="closeout__grid">
            <label className="field">
              <span>Hours</span>
              <input className="input closeout__num" inputMode="decimal" value={String(times.groundInstruction)}
                onChange={(e) => set('groundInstruction', parseHours(e.target.value))} />
            </label>
          </div>
        </section>

        {!isGround && (
          <section className="closeout__section">
            <h3 className="closeout__h">Landings and approaches</h3>
            <div className="closeout__counts">
              {COUNT_FIELDS.map((f) => (
                <div className="closeout__count" key={f.key}>
                  <span>{f.label}</span>
                  <span className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
                    <span className="closeout__countv">{times[f.key]}</span>
                    <Stepper value={times[f.key] as number} min={0} max={99} label={f.label}
                      onChange={(v) => set(f.key, v)} />
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {!isGround && (
          <section className="closeout__section">
            <h3 className="closeout__h">Route</h3>
            <div className="closeout__grid">
              <label className="field">
                <span>From</span>
                <input className="input" value={from} placeholder="KMRB" spellCheck={false}
                  onChange={(e) => setFrom(e.target.value.toUpperCase())} />
              </label>
              <label className="field">
                <span>To</span>
                <input className="input" value={to} placeholder="KMRB" spellCheck={false}
                  onChange={(e) => setTo(e.target.value.toUpperCase())} />
              </label>
              <label className="field" style={{ gridColumn: 'span 2' }}>
                <span>Via</span>
                <input className="input" value={route} placeholder="Practice area"
                  onChange={(e) => setRoute(e.target.value)} />
              </label>
            </div>
          </section>
        )}

        <section className="closeout__section">
          <h3 className="closeout__h">The record</h3>
          <div className="stack" style={{ gap: 14 }}>
            {r.lessonId && (
              <label className="field" style={{ maxWidth: 340 }}>
                <span>Grade for {r.lessonLabel}</span>
                <select className="select" value={grade} onChange={(e) => setGrade(e.target.value as 'S' | 'U' | 'I')}>
                  <option value="S">S · satisfactory</option>
                  <option value="U">U · unsatisfactory, repeat the lesson</option>
                  <option value="I">I · incomplete</option>
                </select>
              </label>
            )}
            <label className="field">
              <span>Remarks</span>
              <textarea className="textarea" value={remarks} onChange={(e) => setRemarks(e.target.value)}
                placeholder="What was covered, how it went, what to work on next." />
              <span className="closeout__hint">Appears in the logbook entry and on the training record.</span>
            </label>
            <label className="field">
              <span>Endorsement (optional)</span>
              <textarea className="textarea" style={{ minHeight: 60 }} value={endorsement}
                onChange={(e) => setEndorsement(e.target.value)}
                placeholder="Text of any endorsement given at this lesson." />
            </label>
          </div>
        </section>

        {(problems.length > 0 || localProblems.length > 0) && (
          <div className="auth__error" role="alert">
            <span>
              {(problems.length ? problems : localProblems).map((x) => (
                <span key={x} style={{ display: 'block' }}>{x}</span>
              ))}
            </span>
          </div>
        )}

        <p className="closeout__hint" style={{ margin: 0 }}>
          Closing out writes the training record, the student’s logbook entry and yours —
          certified with your name and certificate number — and raises the invoice.
        </p>
      </div>
    </Drawer>
  );
}
